import React, { useEffect, useState, useMemo } from 'react';
import { 
  Box, Typography, Table, TableHead, TableRow, TableCell, 
  TableBody, TableContainer, Paper, Button, IconButton, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Chip, MenuItem, TablePagination, Autocomplete, FormControlLabel, Switch
} from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon, Undo as ReturnIcon, Edit as EditIcon } from '@mui/icons-material';
import equipmentService from '../../services/equipmentService';
import { departmentService } from '../../services/api';
import SkeletonBlock from '../ui/SkeletonBlock';
import { useToast } from '../ToastProvider';

export default function EquipmentAssignmentsAdmin() {
  const [assignments, setAssignments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [availableItems, setAvailableItems] = useState([]);
  const [orderedRequests, setOrderedRequests] = useState([]); // Ordered requests for selected employee
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters
  const [filterDeptId, setFilterDeptId] = useState('');
  const [filterCategoryId, setFilterCategoryId] = useState('');
  const [showReturned, setShowReturned] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  
  // Dialogs
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [returnDialog, setReturnDialog] = useState({ open: false, assignment: null, notes: '' });
  const [editDialog, setEditDialog] = useState({ open: false, assignment: null });
  const [editForm, setEditForm] = useState({
    employeeId: null,
    assignedDate: '',
    brand: '',
    model: '',
    serialNumber: '',
    notes: '',
    warrantyExpiration: '',
    equipmentRequestId: ''
  });
  const [editOrderedRequests, setEditOrderedRequests] = useState([]);
  
  // Assign form
  const [assignForm, setAssignForm] = useState({
    employeeId: null,
    useExistingItem: true,
    equipmentItemId: '',
    equipmentRequestId: '', // Link to an ordered request (auto-fulfills it)
    categoryId: '',
    subcategoryId: '',
    brand: '',
    model: '',
    serialNumber: '',
    purchasePrice: '',
    purchaseDate: '',
    supplier: '',
    warrantyExpiration: '',
    notes: ''
  });
  const [filteredSubcategories, setFilteredSubcategories] = useState([]);
  const [saving, setSaving] = useState(false);
  
  const toast = useToast();

  useEffect(() => { loadInitialData(); }, []);
  useEffect(() => { loadAssignments(); }, [filterDeptId, filterCategoryId, showReturned]);

  // Load ordered requests when employee changes
  useEffect(() => {
    if (assignForm.employeeId) {
      equipmentService.getAllRequests('Ordered', assignForm.employeeId)
        .then(data => setOrderedRequests(Array.isArray(data) ? data : []))
        .catch(() => setOrderedRequests([]));
    } else {
      setOrderedRequests([]);
    }
    // Reset request selection when employee changes
    setAssignForm(f => ({ ...f, equipmentRequestId: '' }));
  }, [assignForm.employeeId]);

  useEffect(() => {
    if (assignForm.categoryId) {
      const filtered = subcategories.filter(s => s.categoryId === parseInt(assignForm.categoryId));
      setFilteredSubcategories(filtered);
      // Load available items for this category
      equipmentService.getAvailableItems().then(items => {
        const itemsArr = Array.isArray(items) ? items : [];
        setAvailableItems(itemsArr.filter(i => {
          const sub = subcategories.find(s => s.id === i.subcategoryId);
          return sub && sub.categoryId === parseInt(assignForm.categoryId);
        }));
      }).catch(() => setAvailableItems([]));
    } else {
      setFilteredSubcategories([]);
      setAvailableItems([]);
    }
    setAssignForm(f => ({ ...f, subcategoryId: '', equipmentItemId: '' }));
  }, [assignForm.categoryId, subcategories]);

  const loadInitialData = async () => {
    try {
      const [cats, subs, emps, depts] = await Promise.all([
        equipmentService.getCategories(),
        equipmentService.getSubcategories(),
        departmentService.searchEmployees('', 500),
        departmentService.getAll()
      ]);
      // API interceptor already returns response.data, so use directly
      setCategories(Array.isArray(cats) ? cats : []);
      setSubcategories(Array.isArray(subs) ? subs : []);
      setEmployees(Array.isArray(emps) ? emps : []);
      setDepartments(Array.isArray(depts) ? depts : []);
    } catch (e) { console.error('Failed to load initial data', e); }
  };

  const loadAssignments = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await equipmentService.getAllAssignments({
        departmentId: filterDeptId || null,
        categoryId: filterCategoryId || null,
        includeReturned: showReturned
      });
      setAssignments(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data || e.message || 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const resetAssignForm = () => {
    setAssignForm({
      employeeId: null, useExistingItem: true, equipmentItemId: '', equipmentRequestId: '',
      categoryId: '', subcategoryId: '', brand: '', model: '', serialNumber: '',
      purchasePrice: '', purchaseDate: '', supplier: '', warrantyExpiration: '', notes: ''
    });
    setOrderedRequests([]);
  };

  const handleAssign = async () => {
    if (!assignForm.employeeId) { toast.error('Employee is required'); return; }
    if (assignForm.useExistingItem && !assignForm.equipmentItemId) { toast.error('Select an equipment item'); return; }
    if (!assignForm.useExistingItem && !assignForm.subcategoryId) { toast.error('Subcategory is required'); return; }

    setSaving(true);
    try {
      const payload = { employeeId: assignForm.employeeId };
      if (assignForm.useExistingItem) {
        payload.equipmentItemId = parseInt(assignForm.equipmentItemId);
      } else {
        payload.itemDetails = {
          subcategoryId: parseInt(assignForm.subcategoryId),
          brand: assignForm.brand || null,
          model: assignForm.model || null,
          serialNumber: assignForm.serialNumber || null,
          purchasePrice: assignForm.purchasePrice ? parseFloat(assignForm.purchasePrice) : null,
          purchaseDate: assignForm.purchaseDate || null,
          supplier: assignForm.supplier || null,
          warrantyExpiration: assignForm.warrantyExpiration || null,
          notes: assignForm.notes || null
        };
      }
      // Include request link if selected (will auto-fulfill the request)
      if (assignForm.equipmentRequestId) {
        payload.equipmentRequestId = parseInt(assignForm.equipmentRequestId);
      }
      await equipmentService.assignEquipment(payload);
      const msg = assignForm.equipmentRequestId
        ? 'Equipment assigned and request fulfilled'
        : 'Equipment assigned';
      toast.success(msg);
      setAssignDialogOpen(false);
      resetAssignForm();
      loadAssignments();
    } catch (e) {
      toast.error(e?.response?.data || e.message || 'Failed to assign');
    } finally { setSaving(false); }
  };

  const handleReturn = async () => {
    if (!returnDialog.assignment) return;
    setSaving(true);
    try {
      await equipmentService.revokeAssignment(returnDialog.assignment.id, returnDialog.notes || null);
      toast.success('Equipment returned');
      setReturnDialog({ open: false, assignment: null, notes: '' });
      loadAssignments();
    } catch (e) {
      toast.error(e?.response?.data || e.message || 'Failed to process return');
    } finally { setSaving(false); }
  };

  const openEditDialog = async (assignment) => {
    setEditForm({
      employeeId: assignment.employeeId,
      assignedDate: assignment.assignedDate ? assignment.assignedDate.split('T')[0] : '',
      brand: assignment.brand || '',
      model: assignment.model || '',
      serialNumber: assignment.serialNumber || '',
      notes: assignment.notes || '',
      warrantyExpiration: assignment.warrantyExpiration ? assignment.warrantyExpiration.split('T')[0] : '',
      equipmentRequestId: ''
    });
    setEditDialog({ open: true, assignment });
    // Load ordered requests for this employee
    try {
      const requests = await equipmentService.getAllRequests('Ordered', assignment.employeeId);
      setEditOrderedRequests(Array.isArray(requests) ? requests : []);
    } catch { setEditOrderedRequests([]); }
  };

  const handleEdit = async () => {
    if (!editDialog.assignment) return;
    setSaving(true);
    try {
      const payload = {
        assignedDate: editForm.assignedDate || null,
        brand: editForm.brand || null,
        model: editForm.model || null,
        serialNumber: editForm.serialNumber || null,
        notes: editForm.notes || null,
        warrantyExpiration: editForm.warrantyExpiration || null
      };
      // Include employeeId only if changed (reassignment)
      if (editForm.employeeId && editForm.employeeId !== editDialog.assignment.employeeId) {
        payload.employeeId = editForm.employeeId;
      }
      if (editForm.equipmentRequestId) {
        payload.equipmentRequestId = parseInt(editForm.equipmentRequestId);
      }
      await equipmentService.updateAssignment(editDialog.assignment.id, payload);
      const msg = payload.employeeId
        ? 'Equipment reassigned successfully'
        : (editForm.equipmentRequestId ? 'Assignment updated and request fulfilled' : 'Assignment updated');
      toast.success(msg);
      setEditDialog({ open: false, assignment: null });
      loadAssignments();
    } catch (e) {
      toast.error(e?.response?.data || e.message || 'Failed to update');
    } finally { setSaving(false); }
  };

  const paged = useMemo(() => assignments.slice(page * rowsPerPage, (page + 1) * rowsPerPage), [assignments, page, rowsPerPage]);

  if (loading && assignments.length === 0) return <SkeletonBlock lines={8} />;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5">Equipment Assignments</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <IconButton onClick={loadAssignments} size="small"><RefreshIcon /></IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAssignDialogOpen(true)}>Assign Equipment</Button>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField select size="small" label="Department" value={filterDeptId} onChange={(e) => setFilterDeptId(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="">All</MenuItem>
          {departments.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Category" value={filterCategoryId} onChange={(e) => setFilterCategoryId(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="">All</MenuItem>
          {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
        </TextField>
        <FormControlLabel control={<Switch checked={showReturned} onChange={(e) => setShowReturned(e.target.checked)} size="small" />} label="Show returned" />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Item</TableCell>
              <TableCell>Serial #</TableCell>
              <TableCell>Assigned</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center">No assignments found</TableCell></TableRow>
            ) : paged.map((a) => (
              <TableRow key={a.id} sx={a.isReturned ? { opacity: 0.6 } : {}}>
                <TableCell><strong>{a.employeeName}</strong></TableCell>
                <TableCell>{a.categoryName}</TableCell>
                <TableCell>{a.itemDescription}</TableCell>
                <TableCell>{a.serialNumber || '-'}</TableCell>
                <TableCell>{new Date(a.assignedDate).toLocaleDateString('bg-BG')}</TableCell>
                <TableCell>
                  {a.isReturned ? <Chip label="Returned" color="default" size="small" /> : <Chip label="Active" color="success" size="small" />}
                </TableCell>
                <TableCell align="right">
                  {!a.isReturned && (
                    <>
                      <IconButton size="small" color="primary" onClick={() => openEditDialog(a)} title="Edit Assignment">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="warning" onClick={() => setReturnDialog({ open: true, assignment: a, notes: '' })} title="Process Return">
                        <ReturnIcon />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination component="div" count={assignments.length} page={page} onPageChange={(e, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} rowsPerPageOptions={[10, 15, 25, 50]} />

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Equipment</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={employees}
            getOptionLabel={(o) => o.fullName || o.displayName || `${o.firstName} ${o.lastName}`}
            value={employees.find(e => e.id === assignForm.employeeId) || null}
            onChange={(e, v) => setAssignForm({ ...assignForm, employeeId: v?.id || null })}
            renderInput={(params) => <TextField {...params} label="Employee" required sx={{ mt: 1 }} />}
            sx={{ mb: 2 }}
          />

          {/* Link to ordered request - optional */}
          {orderedRequests.length > 0 && (
            <TextField
              select
              fullWidth
              label="Link to Ordered Request (optional)"
              value={assignForm.equipmentRequestId}
              onChange={(e) => setAssignForm({ ...assignForm, equipmentRequestId: e.target.value })}
              helperText="If selected, the request will be automatically marked as Fulfilled"
              sx={{ mb: 2 }}
            >
              <MenuItem value="">-- None --</MenuItem>
              {orderedRequests.map(r => (
                <MenuItem key={r.id} value={r.id}>
                  {r.subcategoryName} - {r.categoryName} (ordered {new Date(r.orderedDate).toLocaleDateString('bg-BG')})
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField select fullWidth label="Category" value={assignForm.categoryId} onChange={(e) => setAssignForm({ ...assignForm, categoryId: e.target.value })} required sx={{ mb: 2 }}>
            {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </TextField>
          <FormControlLabel control={<Switch checked={assignForm.useExistingItem} onChange={(e) => setAssignForm({ ...assignForm, useExistingItem: e.target.checked })} />} label="Use existing item" sx={{ mb: 2 }} />

          {assignForm.useExistingItem ? (
            <TextField select fullWidth label="Available Item" value={assignForm.equipmentItemId} onChange={(e) => setAssignForm({ ...assignForm, equipmentItemId: e.target.value })} required sx={{ mb: 2 }} disabled={!assignForm.categoryId}>
              {availableItems.map(i => <MenuItem key={i.id} value={i.id}>{i.subcategoryName} - {i.brand} {i.model} {i.serialNumber ? `(${i.serialNumber})` : ''}</MenuItem>)}
            </TextField>
          ) : (
            <>
              <TextField select fullWidth label="Item Type" value={assignForm.subcategoryId} onChange={(e) => setAssignForm({ ...assignForm, subcategoryId: e.target.value })} required sx={{ mb: 2 }} disabled={!assignForm.categoryId}>
                {filteredSubcategories.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
              </TextField>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField label="Brand" value={assignForm.brand} onChange={(e) => setAssignForm({ ...assignForm, brand: e.target.value })} sx={{ flex: 1 }} />
                <TextField label="Model" value={assignForm.model} onChange={(e) => setAssignForm({ ...assignForm, model: e.target.value })} sx={{ flex: 1 }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField label="Serial Number" value={assignForm.serialNumber} onChange={(e) => setAssignForm({ ...assignForm, serialNumber: e.target.value })} sx={{ flex: 1 }} />
                <TextField type="number" label="Purchase Price" value={assignForm.purchasePrice} onChange={(e) => setAssignForm({ ...assignForm, purchasePrice: e.target.value })} sx={{ flex: 1 }} InputProps={{ inputProps: { min: 0, step: 0.01 } }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField type="date" label="Purchase Date" value={assignForm.purchaseDate} onChange={(e) => setAssignForm({ ...assignForm, purchaseDate: e.target.value })} sx={{ flex: 1 }} InputLabelProps={{ shrink: true }} />
                <TextField type="date" label="Warranty Expiration" value={assignForm.warrantyExpiration} onChange={(e) => setAssignForm({ ...assignForm, warrantyExpiration: e.target.value })} sx={{ flex: 1 }} InputLabelProps={{ shrink: true }} />
              </Box>
              <TextField fullWidth label="Supplier" value={assignForm.supplier} onChange={(e) => setAssignForm({ ...assignForm, supplier: e.target.value })} sx={{ mb: 2 }} />
            </>
          )}
          <TextField fullWidth multiline rows={2} label="Notes" value={assignForm.notes} onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAssignDialogOpen(false); resetAssignForm(); }}>Cancel</Button>
          <Button onClick={handleAssign} variant="contained" disabled={saving}>{saving ? 'Assigning...' : 'Assign'}</Button>
        </DialogActions>
      </Dialog>

      {/* Return Dialog */}
      <Dialog open={returnDialog.open} onClose={() => setReturnDialog({ open: false, assignment: null, notes: '' })}>
        <DialogTitle>Process Equipment Return</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Return <strong>{returnDialog.assignment?.itemDescription}</strong> from <strong>{returnDialog.assignment?.employeeName}</strong>?</Typography>
          <TextField fullWidth multiline rows={2} label="Notes (optional)" value={returnDialog.notes} onChange={(e) => setReturnDialog({ ...returnDialog, notes: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReturnDialog({ open: false, assignment: null, notes: '' })}>Cancel</Button>
          <Button onClick={handleReturn} color="warning" variant="contained" disabled={saving}>{saving ? 'Processing...' : 'Process Return'}</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, assignment: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Assignment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Editing: <strong>{editDialog.assignment?.itemDescription}</strong>
          </Typography>

          {/* Employee (Reassign) */}
          <Autocomplete
            options={employees}
            getOptionLabel={(o) => o.fullName || o.displayName || `${o.firstName} ${o.lastName}`}
            value={employees.find(e => e.id === editForm.employeeId) || null}
            onChange={(e, v) => setEditForm({ ...editForm, employeeId: v?.id || null })}
            renderInput={(params) => <TextField {...params} label="Assigned To" required />}
            sx={{ mb: 2, mt: 1 }}
          />

          <TextField
            fullWidth
            type="date"
            label="Assigned Date"
            value={editForm.assignedDate}
            onChange={(e) => setEditForm({ ...editForm, assignedDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />

          <TextField fullWidth label="Brand" value={editForm.brand} onChange={(e) => setEditForm({ ...editForm, brand: e.target.value })} sx={{ mb: 2 }} />
          <TextField fullWidth label="Model" value={editForm.model} onChange={(e) => setEditForm({ ...editForm, model: e.target.value })} sx={{ mb: 2 }} />
          <TextField fullWidth label="Serial Number" value={editForm.serialNumber} onChange={(e) => setEditForm({ ...editForm, serialNumber: e.target.value })} sx={{ mb: 2 }} />

          <TextField
            fullWidth
            type="date"
            label="Warranty Expiration"
            value={editForm.warrantyExpiration}
            onChange={(e) => setEditForm({ ...editForm, warrantyExpiration: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />

          <TextField fullWidth multiline rows={2} label="Notes" value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} sx={{ mb: 2 }} />

          {/* Link to ordered request - optional */}
          {editOrderedRequests.length > 0 && (
            <TextField
              select
              fullWidth
              label="Link to Ordered Request (optional)"
              value={editForm.equipmentRequestId}
              onChange={(e) => setEditForm({ ...editForm, equipmentRequestId: e.target.value })}
              helperText="If selected, the request will be automatically marked as Fulfilled"
            >
              <MenuItem value="">-- None --</MenuItem>
              {editOrderedRequests.map(r => (
                <MenuItem key={r.id} value={r.id}>
                  {r.subcategoryName} - {r.categoryName} (ordered {new Date(r.orderedDate).toLocaleDateString('bg-BG')})
                </MenuItem>
              ))}
            </TextField>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, assignment: null })}>Cancel</Button>
          <Button onClick={handleEdit} variant="contained" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

